import React from "react";
import { Link } from "react-router-dom";
import { openCookieSettings } from "@/components/CookieConsent";
import { EnvelopeSimpleIcon, ShieldCheckIcon } from "@phosphor-icons/react";
import { RISK_TEXT } from "./RiskNotice";

const YEAR = new Date().getFullYear();
const SUPPORT_EMAIL = "botgenomix.support@gmail.com";

/**
 * Neutral Phase-2 footer — no spread-bet / tax-free references.
 * Used across Hub, MT5 landing, cTrader landing.
 */
export default function HubFooter() {
  return (
    <footer
      data-testid="hub-footer"
      className="relative border-t border-[#1A1C24] bg-[#04050A]"
    >
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr_1fr] gap-10">
          <div>
            <Link to="/" className="flex items-center gap-3" data-testid="footer-logo-link">
              <img src="/botgenomix-logo-512.png" alt="BotGenomix" className="h-14 w-14 rounded-xl" />
              <div>
                <div className="text-xl font-medium text-[#F1F5F9]">
                  Bot<span className="text-[#D6FF38]">Genomix</span>
                </div>
                <div className="mono text-[10px] uppercase tracking-widest text-[#64748B] mt-0.5">
                  MT5 trading software
                </div>
              </div>
            </Link>
            <p className="mt-6 text-sm text-[#94A3B8] max-w-sm leading-relaxed">
              BotGenomix develops, tests and licenses downloadable automated trading software for MetaTrader 5.
              Customers install and run it on their own MT5 accounts, with brokers they choose.
            </p>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="footer-email"
              className="mt-5 inline-flex items-center gap-2 border border-[#1A1C24] bg-[#0B0D14] hover:border-[#94A3B8] transition-colors px-3 py-2 rounded-md mono text-[11px] text-[#F1F5F9]"
            >
              <EnvelopeSimpleIcon size={14} className="text-[#D6FF38]" />
              {SUPPORT_EMAIL}
            </a>
          </div>

          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-[#D6FF38]">Products</div>
            <ul className="mt-4 space-y-2.5">
              <li>
                <Link to="/mt5" data-testid="footer-link-mt5" className="inline-block py-1.5 text-sm text-[#94A3B8] hover:text-[#F1F5F9] transition-colors">
                  MT5 Expert Advisors
                </Link>
              </li>
              <li>
                <Link to="/pricing" data-testid="footer-link-pricing" className="inline-block py-1.5 text-sm text-[#94A3B8] hover:text-[#F1F5F9] transition-colors">
                  Software licence plans
                </Link>
              </li>
              <li>
                <Link to="/track-record" data-testid="footer-link-track" className="inline-block py-1.5 text-sm text-[#94A3B8] hover:text-[#F1F5F9] transition-colors">
                  Demo track record
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-[#D6FF38]">Company</div>
            <ul className="mt-4 space-y-2.5">
              <li><Link to="/account" data-testid="footer-link-account" className="inline-block py-1.5 text-sm text-[#94A3B8] hover:text-[#F1F5F9] transition-colors">My account</Link></li>
              <li><button type="button" onClick={openCookieSettings} data-testid="footer-cookie-settings" className="text-sm text-[#94A3B8] hover:text-[#D6FF38] transition-colors">Cookie settings</button></li>
              <li><Link to="/legal" data-testid="footer-link-legal" className="inline-block py-1.5 text-sm text-[#94A3B8] hover:text-[#F1F5F9] transition-colors">Risk, terms & refunds</Link></li>
              <li><Link to="/mt5#how-we-test" data-testid="footer-link-how" className="inline-block py-1.5 text-sm text-[#94A3B8] hover:text-[#F1F5F9] transition-colors">How we test</Link></li>
              <li>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  data-testid="footer-link-contact"
                  className="inline-block py-1.5 text-sm text-[#94A3B8] hover:text-[#F1F5F9] transition-colors break-all"
                >
                  Contact us
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-[#1A1C24] flex flex-col md:flex-row justify-between gap-4 items-start md:items-center">
          <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">
            © {YEAR} BotGenomix. All rights reserved.
          </div>
          <div className="flex items-center gap-4 mono text-[10px] uppercase tracking-widest text-[#64748B]">
            <span className="flex items-center gap-1">
              <ShieldCheckIcon size={12} className="text-[#2ECA7F]" /> No code shared
            </span>
          </div>
        </div>

        <div className="mt-8 mono text-[10px] leading-relaxed text-[#475569] space-y-2">
          <p>
            <span className="text-[#FBBF24] uppercase tracking-widest">Risk disclosure</span> —{" "}
            {RISK_TEXT} Leveraged products such as CFDs can lose money rapidly, and most retail investor
            accounts lose money when trading CFDs.
          </p>
          <p>
            BotGenomix is a software developer. We do not hold customer funds, execute trades on your behalf,
            manage trading accounts, recommend brokers or provide investment advice. BotGenomix is not authorised
            or regulated by the Financial Conduct Authority.
          </p>
          <p>
            Store bot statistics are hypothetical results from MetaTrader 5 backtests, produced with hindsight from a
            £2,000 start balance, and do not include every real-world cost. Track-record and dashboard figures come
            from demo or customer MT5 accounts and are not a guarantee of future results. <Link to="/legal#hypothetical" className="underline inline-block py-2 hover:text-[#F1F5F9]">Read how to interpret them</Link>.
          </p>
        </div>
      </div>
    </footer>
  );
}
